/**
 * Liantiao GO Access Hub — /hub
 * Phase 2H · Release 17 (2026-04-29)
 *
 * UI-only enhancement. Pure launcher. No auth, no permission gating, no
 * auto-login, no redirect. Each tile is a plain <Link>. Suggestions only
 * highlight tiles via CSS — they never route or authenticate.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, PackageCheck, Truck, Store, Headphones,
  UserCheck, Building, Boxes, Users,
  CalendarClock, Plus,
  MapPin, Sparkles,
  KeyRound, ShieldCheck,
  ExternalLink, RotateCcw, Search, Star,
  Gauge, ClipboardList,
} from "lucide-react";

/* ─── Group palettes ──────────────────────────────────────────────────────
   Each group has matching subtle background, border, headline + badge color.
   Text always uses slate-800/900 for high contrast.
*/
const PALETTES = {
  ops: {
    name: "Operations",
    helper: "Day-to-day shipping and customer-facing flows.",
    headline: "text-sky-700",
    rule: "bg-sky-500",
    sectionBg: "bg-sky-50/60",
    sectionBorder: "border-sky-100",
    tileBg: "bg-white",
    tileBorder: "border-sky-200",
    tileHover: "hover:border-sky-400 hover:shadow-md",
    iconBg: "bg-sky-100 text-sky-700",
    cta: "text-sky-700 group-hover:text-sky-900",
    pill: "bg-sky-600",
  },
  pudo: {
    name: "PUDO Network",
    helper: "Pickup-and-drop-off recruitment, onboarding and station ops.",
    headline: "text-emerald-700",
    rule: "bg-emerald-500",
    sectionBg: "bg-emerald-50/60",
    sectionBorder: "border-emerald-100",
    tileBg: "bg-white",
    tileBorder: "border-emerald-200",
    tileHover: "hover:border-emerald-400 hover:shadow-md",
    iconBg: "bg-emerald-100 text-emerald-700",
    cta: "text-emerald-700 group-hover:text-emerald-900",
    pill: "bg-emerald-600",
  },
  field: {
    name: "Field & Growth",
    helper: "Field-agent productivity and on-the-road business onboarding.",
    headline: "text-orange-700",
    rule: "bg-orange-500",
    sectionBg: "bg-orange-50/60",
    sectionBorder: "border-orange-100",
    tileBg: "bg-white",
    tileBorder: "border-orange-200",
    tileHover: "hover:border-orange-400 hover:shadow-md",
    iconBg: "bg-orange-100 text-orange-700",
    cta: "text-orange-700 group-hover:text-orange-900",
    pill: "bg-orange-600",
  },
  intel: {
    name: "Intelligence & Mapping",
    helper: "Improve Jamaica routing and pricing accuracy through verified addresses.",
    headline: "text-purple-700",
    rule: "bg-purple-500",
    sectionBg: "bg-purple-50/60",
    sectionBorder: "border-purple-200",
    tileBg: "bg-white",
    tileBorder: "border-purple-200",
    tileHover: "hover:border-purple-400 hover:shadow-md",
    iconBg: "bg-purple-100 text-purple-700",
    cta: "text-purple-700 group-hover:text-purple-900",
    pill: "bg-purple-600",
  },
  fleet: {
    name: "Fleet & Vehicle Ops",
    helper: "Daily mileage capture and off-system income/expense entry for Liantiao-owned vehicles.",
    headline: "text-amber-800",
    rule: "bg-amber-500",
    sectionBg: "bg-amber-50/60",
    sectionBorder: "border-amber-200",
    tileBg: "bg-white",
    tileBorder: "border-amber-200",
    tileHover: "hover:border-amber-500 hover:shadow-md",
    iconBg: "bg-amber-100 text-amber-800",
    cta: "text-amber-800 group-hover:text-amber-900",
    pill: "bg-amber-600",
  },
  system: {
    name: "System Access",
    helper: "Universal login and admin control.",
    headline: "text-slate-700",
    rule: "bg-slate-700",
    sectionBg: "bg-slate-100/70",
    sectionBorder: "border-slate-200",
    tileBg: "bg-white",
    tileBorder: "border-slate-300",
    tileHover: "hover:border-slate-500 hover:shadow-md",
    iconBg: "bg-slate-200 text-slate-800",
    cta: "text-slate-700 group-hover:text-slate-900",
    pill: "bg-slate-700",
  },
};

/* ─── Hub-eligible portals (customer-friendly labels) ─────────────────── */
const SECTIONS = [
  {
    key: "ops",
    testid: "hub-section-ops",
    tiles: [
      { id: "biz-quick",     testid: "hub-tile-biz-quick",     title: "Book Business Deliveries", purpose: "Send packages to PUDO drop-offs. Login with your business PIN.",        icon: PackageCheck, href: "https://liantiaoja.com/business-quick/login", external: true },
      { id: "driver",        testid: "hub-tile-driver",        title: "Driver Workspace",         purpose: "Accept jobs, scan packages, track your earnings.",                       icon: Truck,        href: "/login?next=/driver" },
      { id: "seller",        testid: "hub-tile-seller",        title: "Seller Products",          purpose: "Manage your store, products, fulfilment, and earnings.",                 icon: Store,        href: "/login?next=/merchant/products" },
      { id: "agent-booking", testid: "hub-tile-agent-booking", title: "Assisted Booking Console", purpose: "Book rides and packages on behalf of customers.",                        icon: Headphones,   href: "/agent/login" },
    ],
  },
  {
    key: "pudo",
    testid: "hub-section-pudo",
    tiles: [
      { id: "pudo-field-agent", testid: "hub-tile-pudo-field-agent", title: "PUDO Field Agent",          purpose: "Onboard new PUDO stations across Jamaica. PIN login.",                  icon: UserCheck, href: "/portal/pudo-agent" },
      { id: "pudo-onboarding",  testid: "hub-tile-pudo-onboarding",  title: "Become a PUDO",             purpose: "Self-service login for businesses applying to host a PUDO station.",   icon: Building,  href: "/portal/pudo-onboard" },
      { id: "pudo-owner",       testid: "hub-tile-pudo-owner",       title: "PUDO Operator (Owner)",     purpose: "Approved PUDO owners — full station control, finance, audit.",        icon: Boxes,     href: "/portal/pudo-onboard/operator-login" },
      { id: "pudo-staff",       testid: "hub-tile-pudo-staff",       title: "PUDO Operator (Staff)",     purpose: "Per-staff PIN login for clerks at an approved PUDO.",                  icon: Users,     href: "/portal/pudo-onboard/operator-staff-login" },
    ],
  },
  {
    key: "field",
    testid: "hub-section-field",
    tiles: [
      { id: "fa-rollup", testid: "hub-tile-fa-rollup", title: "Weekly Activity Rollup",  purpose: "Daily activity logging and weekly KPI rollup for field agents.", icon: CalendarClock, href: "/portal/field-agent/weekly-rollup" },
      { id: "fa-bq",     testid: "hub-tile-fa-bq",     title: "Sign Up Shops On The Road", purpose: "Field-agent quick-add for Business Quick shops.",              icon: Plus,          href: "/portal/field-agent/business-quick" },
    ],
  },
  {
    key: "intel",
    testid: "hub-section-intel",
    tiles: [
      { id: "loc-capture", testid: "hub-tile-loc-capture", title: "Verify Address / Improve Map", purpose: "Capture pins, communities, schemes and aliases. Powers GO routing & pricing.", icon: MapPin,    href: "/admin/location-capture", priority: true, badge: "Important — improves Jamaica address accuracy" },
      { id: "loc-intel",   testid: "hub-tile-loc-intel",   title: "Map Quality Dashboard",        purpose: "Confidence tiers, decay management, location intelligence health.",            icon: Sparkles,  href: "/admin/location-intelligence" },
    ],
  },
  {
    key: "fleet",
    testid: "hub-section-fleet",
    tiles: [
      { id: "driver-mileage", testid: "hub-tile-driver-mileage", title: "Vehicle Mileage Log", purpose: "Rohan: log opening and closing odometer for the Probox each day.",     icon: Gauge,         href: "/driver/vehicle-log" },
      { id: "vops-capture",   testid: "hub-tile-vops-capture",   title: "Capture Off-System Packages",   purpose: "Admin: record cash / off-platform income and vehicle expenses for the Probox.", icon: ClipboardList, href: "/admin/vehicle-ops" },
    ],
  },
  {
    key: "system",
    testid: "hub-section-system",
    tiles: [
      { id: "login", testid: "hub-tile-login", title: "Universal Login",  purpose: "One login, role-routed. Staff, drivers, sellers and customers.", icon: KeyRound,    href: "/login" },
      { id: "approvals", testid: "hub-tile-approvals", title: "Karishma proposals", purpose: "Admin: approve drivers or vehicles proposed by Karishma.", icon: ShieldCheck, href: "/admin/approvals" },
      { id: "admin", testid: "hub-tile-admin", title: "Admin Dashboard",  purpose: "Admin home — control tower for every operational surface.",     icon: ShieldCheck, href: "/admin" },
    ],
  },
];

/* ─── "Find my workspace" suggestion sets (highlight-only, NO routing) ─── */
const SUGGESTIONS = [
  { id: "driver", testid: "hub-suggest-driver", label: "I am a driver",               ids: ["driver"] },
  { id: "seller", testid: "hub-suggest-seller", label: "I am a seller",               ids: ["seller"] },
  { id: "pudo",   testid: "hub-suggest-pudo",   label: "I work with PUDO",            ids: ["pudo-field-agent", "pudo-onboarding", "pudo-owner", "pudo-staff"] },
  { id: "field",  testid: "hub-suggest-field",  label: "I am a field agent",          ids: ["fa-rollup", "fa-bq", "loc-capture"] },
  { id: "fleet",  testid: "hub-suggest-fleet",  label: "I'm Rohan — vehicle / mileage", ids: ["driver-mileage", "vops-capture"] },
  { id: "ops",    testid: "hub-suggest-ops",    label: "I manage operations",         ids: ["admin", "loc-intel", "biz-quick"] },
  { id: "biz",    testid: "hub-suggest-biz",    label: "I am booking for a business", ids: ["biz-quick"] },
];

function Tile({ tile, palette, highlighted, dimmed }) {
  const Icon = tile.icon;
  const ringPriority = tile.priority ? "ring-2 ring-purple-500/70" : "";
  const ringSuggest = highlighted ? "ring-2 ring-sky-500 shadow-lg shadow-sky-100" : "";
  const fade = dimmed ? "opacity-40" : "";
  const inner = (
    <div
      data-testid={tile.testid}
      data-priority={tile.priority ? "true" : "false"}
      data-highlight={highlighted ? "true" : "false"}
      className={`group relative h-full min-h-[148px] flex flex-col gap-3 rounded-2xl p-5 transition-all border-2 ${palette.tileBg} ${palette.tileBorder} ${palette.tileHover} ${ringPriority} ${ringSuggest} ${fade}`}
    >
      {tile.priority && (
        <span
          data-testid="hub-priority-badge"
          className={`absolute -top-2.5 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full text-white shadow ${palette.pill}`}
        >
          <Star className="w-3 h-3 fill-current" /> Recommended
        </span>
      )}
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${palette.iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-sm sm:text-base leading-snug text-slate-900">{tile.title}</h3>
        {tile.badge && (
          <p className="mt-1 text-[11px] font-semibold text-purple-700">{tile.badge}</p>
        )}
        <p className="mt-1 text-xs sm:text-sm text-slate-700 leading-snug">{tile.purpose}</p>
      </div>
      <div className={`flex items-center gap-1 text-xs font-semibold ${palette.cta}`}>
        Open Portal {tile.external && <ExternalLink className="w-3 h-3" />}
      </div>
    </div>
  );
  return tile.external ? (
    <a href={tile.href} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a>
  ) : (
    <Link to={tile.href} className="block h-full">{inner}</Link>
  );
}

export default function Hub() {
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const highlightSet = activeSuggestion
    ? new Set(SUGGESTIONS.find((s) => s.id === activeSuggestion)?.ids || [])
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 [color-scheme:light]" data-testid="hub-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Liantiao GO Access Hub</h1>
          <p className="mt-1 text-sm sm:text-base text-slate-600">Choose your workspace to continue.</p>
        </header>

        {/* Find my workspace */}
        <section
          data-testid="hub-find-my-workspace"
          className="mb-8 bg-white border-2 border-sky-100 rounded-2xl p-4 sm:p-5 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
              <Search className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-sm sm:text-base text-slate-900">Find my workspace</h2>
              <p className="text-xs sm:text-sm text-slate-600">Choose what best describes you and we'll point you to the right portal.</p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s.id}
                data-testid={s.testid}
                onClick={() => setActiveSuggestion(s.id)}
                className={`min-h-[42px] text-xs sm:text-sm px-4 py-2 rounded-full border-2 font-medium transition ${
                  activeSuggestion === s.id
                    ? "bg-sky-600 text-white border-sky-600 shadow"
                    : "bg-white text-slate-800 border-slate-200 hover:border-sky-400 hover:bg-sky-50"
                }`}
              >
                {s.label}
              </button>
            ))}
            {activeSuggestion && (
              <button
                data-testid="hub-show-all-portals"
                onClick={() => setActiveSuggestion(null)}
                className="min-h-[42px] text-xs sm:text-sm px-4 py-2 rounded-full border-2 border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200 inline-flex items-center gap-1 font-medium"
              >
                <RotateCcw className="w-3 h-3" /> Show all portals
              </button>
            )}
          </div>
        </section>

        {/* Sections */}
        <div className="space-y-6 sm:space-y-8">
          {SECTIONS.map((section) => {
            const palette = PALETTES[section.key];
            return (
              <section
                key={section.key}
                data-testid={section.testid}
                className={`rounded-2xl border ${palette.sectionBorder} ${palette.sectionBg} p-4 sm:p-5`}
              >
                <header className="mb-4">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-5 rounded-full ${palette.rule}`} aria-hidden="true" />
                    <h2 className={`text-sm sm:text-base font-bold uppercase tracking-wider ${palette.headline}`}>
                      {palette.name}
                    </h2>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1 ml-3.5">{palette.helper}</p>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {section.tiles.map((tile) => {
                    const highlighted = highlightSet ? highlightSet.has(tile.id) : false;
                    const dimmed = highlightSet ? !highlightSet.has(tile.id) : false;
                    return <Tile key={tile.id} tile={tile} palette={palette} highlighted={highlighted} dimmed={dimmed} />;
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <footer className="mt-10 text-center text-[11px] text-slate-400">
          No authentication on this page. Each portal keeps its own login.
        </footer>
      </div>
    </div>
  );
}
